/**
 * « Performances over time » — Cost en bâtons, ROAS en points reliés.
 *
 * ── Deux panneaux, pas deux axes ────────────────────────────────────────────
 * Cost (des centaines d'euros) et ROAS (autour de ×1,5) n'ont aucune échelle
 * commune. Les superposer imposerait un double axe vertical, où l'endroit où
 * les séries se croisent ne dépend que des échelles choisies — et se lit comme
 * une relation qui n'existe pas. Deux panneaux empilés, alignés sur le même
 * axe de temps et synchronisés au survol, montrent la même chose sans ce
 * mensonge.
 *
 * Rose (emplacement 1) pour Cost, jaune (palette catégorielle) pour ROAS :
 * paire validée sur la surface sombre ; l'écart en deutéranopie (ΔE 6,4) est
 * dans la bande tolérée grâce au double codage — bâtons contre points reliés,
 * un panneau chacun, légende nommée.
 *
 * ROAS d'une semaine = valeur de la semaine ÷ coût de la semaine, jamais la
 * moyenne des ROAS quotidiens.
 */

import { adsRoas } from '@shared/types/sales';
import { useMemo } from 'react';
import { Bar, BarChart, CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';

import { formatDayLong, formatDayShort, formatMoney, formatMoneyCompact } from '../lib/format';
import { bucketSizeFor } from '../lib/sales';
import { AXIS_STYLE, BAR_MAX_SIZE, BAR_RADIUS, CATEGORICAL_COLORS, COLORS, LINE_WIDTH } from '../lib/theme';
import type { DataColumn } from '../types';
import { ChartCard } from './Card';
import { ChartLegend } from './ChartTooltip';
import { useChartMarkers } from '../hooks/useChartMarkers';
import { MarkerStrip } from './MarkerStrip';

export interface CostValuePoint {
  date: string;
  cost: number;
  value: number;
}

interface Bucket {
  date: string;
  cost: number;
  value: number;
  roas: number | null;
}

const COST_COLOR = COLORS.series1;
const ROAS_COLOR = CATEGORICAL_COLORS[4];
const ratioFormatter = new Intl.NumberFormat('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const formatRoas = (value: number) => `×${ratioFormatter.format(value)}`;

function BucketTooltip({
  active,
  payload,
  bucketSize,
  currency,
}: {
  active?: boolean;
  payload?: Array<{ payload?: Bucket }>;
  bucketSize: number;
  currency: string;
}) {
  const bucket = payload?.[0]?.payload;
  if (!active || !bucket) return null;
  return (
    <div className="tooltip" role="tooltip">
      <p className="tooltip__title">
        {bucketSize === 1 ? formatDayLong(bucket.date) : `Semaine à partir du ${formatDayLong(bucket.date)}`}
      </p>
      <div className="tooltip__row">
        <span className="legend__key legend__key--area" style={{ background: COST_COLOR }} aria-hidden="true" />
        <span className="tooltip__name">Cost</span>
        <span className="tooltip__value">{formatMoney(bucket.cost, currency)}</span>
      </div>
      <div className="tooltip__row">
        <span className="legend__key" style={{ background: ROAS_COLOR }} aria-hidden="true" />
        <span className="tooltip__name">ROAS</span>
        <span className="tooltip__value">{bucket.roas !== null ? formatRoas(bucket.roas) : '—'}</span>
      </div>
    </div>
  );
}


/** Marqueurs « ventes » : impact lu sur la dépense. */
const MARKER_CONFIG = {
  value: (bucket: Bucket) => bucket.cost,
  mode: 'level',
  unit: 'count',
  higherIsBetter: false,
  scope: 'sales',
} as const;

export function CostRoasChart({ points, currency, subtitle }: { points: readonly CostValuePoint[]; currency: string; subtitle: string }) {
  const bucketSize = bucketSizeFor(points.length) === 1 ? 1 : 7;

  const buckets = useMemo<Bucket[]>(() => {
    const out: Bucket[] = [];
    for (let start = 0; start < points.length; start += bucketSize) {
      const slice = points.slice(start, start + bucketSize);
      const first = slice[0];
      if (!first) continue;
      const cost = slice.reduce((sum, point) => sum + point.cost, 0);
      const value = slice.reduce((sum, point) => sum + point.value, 0);
      out.push({
        date: first.date,
        cost: Math.round(cost * 100) / 100,
        value: Math.round(value * 100) / 100,
        roas: adsRoas(value, cost),
      });
    }
    return out;
  }, [points, bucketSize]);

  const markers = useChartMarkers(buckets, MARKER_CONFIG);

  const columns: ReadonlyArray<DataColumn<Bucket>> = [
    { key: 'date', header: bucketSize === 1 ? 'Jour' : 'Semaine du', render: (row) => formatDayLong(row.date) },
    { key: 'cost', header: 'Cost HT', numeric: true, render: (row) => formatMoney(row.cost, currency) },
    { key: 'value', header: 'Conversion Value TTC', numeric: true, render: (row) => formatMoney(row.value, currency) },
    { key: 'roas', header: 'ROAS HT', numeric: true, render: (row) => (row.roas !== null ? formatRoas(row.roas) : '—') },
  ];

  const axisTick = { fill: AXIS_STYLE.fill, fontSize: AXIS_STYLE.fontSize };
  const hasSpend = buckets.some((bucket) => bucket.cost > 0);

  return (
    <ChartCard
      footer={<MarkerStrip items={markers.items} />}
      title="Performances over time"
      subtitle={`${subtitle}, ${bucketSize === 1 ? 'par jour' : 'par semaine'}`}
      className="chart-grid__wide"
      rows={hasSpend ? buckets : []}
      columns={columns}
      emptyMessage="Aucune dépense publicitaire sur cette période."
      chartHeight={360}
    >
      <ChartLegend
        entries={[
          { label: 'Cost (HT)', color: COST_COLOR, shape: 'area' },
          { label: 'ROAS (HT)', color: ROAS_COLOR },
        ]}
      />
      <div style={{ height: '58%' }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={buckets} syncId="cost-roas" margin={{ top: 4, right: 12, bottom: 0, left: 0 }}>
            <CartesianGrid stroke={COLORS.grid} vertical={false} />
            {/* Pas d'étiquettes de dates ici : elles sont sous le panneau du bas. */}
            <XAxis dataKey="date" hide />
            <YAxis tickFormatter={(value: number) => formatMoneyCompact(value, currency)} tick={axisTick} axisLine={false} tickLine={false} width={60} />
            <Tooltip cursor={{ fill: 'rgb(255 255 255 / 0.04)' }} content={<BucketTooltip bucketSize={bucketSize} currency={currency} />} />
            {markers.lines}
            <Bar
              dataKey="cost"
              name="Cost"
              fill={COST_COLOR}
              radius={[BAR_RADIUS, BAR_RADIUS, 0, 0]}
              maxBarSize={BAR_MAX_SIZE}
              isAnimationActive={false}
            />
          </BarChart>
        </ResponsiveContainer>
      </div>
      <div style={{ height: '34%' }}>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={buckets} syncId="cost-roas" margin={{ top: 10, right: 12, bottom: 0, left: 0 }}>
            <CartesianGrid stroke={COLORS.grid} vertical={false} />
            <XAxis dataKey="date" tickFormatter={formatDayShort} tick={axisTick} axisLine={{ stroke: COLORS.grid }} tickLine={false} minTickGap={32} />
            <YAxis tickFormatter={formatRoas} tick={axisTick} axisLine={false} tickLine={false} width={60} domain={[0, 'auto']} />
            <Tooltip cursor={{ stroke: COLORS.textMuted, strokeWidth: 1 }} content={<BucketTooltip bucketSize={bucketSize} currency={currency} />} />
            {markers.lines}
            <Line
              type="monotone"
              dataKey="roas"
              name="ROAS"
              stroke={ROAS_COLOR}
              strokeWidth={LINE_WIDTH}
              // Points visibles sur chaque jour : c'est la demande (« points
              // reliés »), et ils restent lisibles jusqu'à ~31 points ; au-delà
              // on passe à la semaine.
              dot={{ r: 4, fill: ROAS_COLOR, stroke: COLORS.surface, strokeWidth: 2 }}
              activeDot={{ r: 5, fill: ROAS_COLOR, stroke: COLORS.surface, strokeWidth: 2 }}
              connectNulls={false}
              isAnimationActive={false}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </ChartCard>
  );
}
